import React, { ReactElement, ReactNode } from 'react';
import { Tag as AntTag } from 'antd';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';

const { CheckableTag: AntCheckableTag } = AntTag;

type CheckableTagGroupValueType = string | number;

interface CustomStyle {
  tag: {
    marginInlineEnd: CSSSize;
  };
}
interface CheckableTagOption<ValueType extends CheckableTagGroupValueType> {
  value: ValueType;
  label: string | ReactNode;
  icon?: ReactNode;
}

interface CheckableTagGroupProps<ValueType extends CheckableTagGroupValueType> {
  options: Array<CheckableTagOption<ValueType>>;
  value?: ValueType | ValueType[];
  onChange?: (value: ValueType | ValueType[]) => void;
  className?: string;
  multiple?: boolean;
  customStyle?: CustomStyle;
}

function CheckableTagGroup<ValueType extends CheckableTagGroupValueType>({
  options,
  value,
  onChange,
  className,
  multiple = false,
  customStyle,
}: CheckableTagGroupProps<ValueType>): ReactElement {
  const isChecked = (optionValue: ValueType): boolean => {
    if (multiple && Array.isArray(value)) {
      return value.includes(optionValue);
    }
    return value === optionValue;
  };

  const handleChange = (optionValue: ValueType) => {
    if (!onChange) return;

    if (multiple) {
      const currentValues = (Array.isArray(value) ? value : []) as ValueType[];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter(v => v !== optionValue)
        : [...currentValues, optionValue];
      onChange(newValues);
    } else {
      onChange(optionValue);
    }
  };

  return (
    <CheckableTagGroupWrapper className={className}>
      {options.map(option => (
        <StyledCheckableTag
          key={option.value}
          checked={isChecked(option.value)}
          onChange={() => handleChange(option.value)}
          customStyle={customStyle}
        >
          <ContentWrapper>
            {option.icon && <IconWrapper>{option.icon}</IconWrapper>}
            <LabelWrapper>{option.label}</LabelWrapper>
          </ContentWrapper>
        </StyledCheckableTag>
      ))}
    </CheckableTagGroupWrapper>
  );
}

const CheckableTagGroupWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

const IconWrapper = styled.div`
  margin-right: 4px;
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: baseline;
`;

const LabelWrapper = styled.div``;

const StyledCheckableTag = styled(AntCheckableTag)<{ customStyle?: CustomStyle }>`
  &.ant-tag {
    margin-inline-end: ${props => props.customStyle?.tag.marginInlineEnd};
  }

  &.ant-tag-checkable {
    border: 0;
    background-color: ${colorsPalette.white};
    color: ${colorsPalette.gray600};
    cursor: pointer;
    user-select: none;
    padding: 0 11px;
    height: 22px;
    line-height: 20px;
    display: inline-flex;
    align-items: center;
    border-radius: 4px;
    transition: all 0.3s;

    &:hover {
      color: ${colorsPalette.gray600};
      border-color: ${colorsPalette.gray600};
    }

    &.ant-tag-checkable.ant-tag-checkable-checked {
      background-color: ${colorsPalette.dustyBlue500};
      border-color: ${colorsPalette.dustyBlue500};
      color: ${colorsPalette.white};

      img {
        filter: brightness(0) invert(1);
      }

      &:hover {
        background-color: ${colorsPalette.dustyBlue500};
        border-color: ${colorsPalette.dustyBlue500};
      }
    }
  }
`;

export { CheckableTagGroup };
